import { useEffect, useMemo, useRef, useState } from "react";
import Tabs from "../../../DesignSystem/Tabs/Tabs";
import CustomSelect from "../../../DesignSystem/Dropdowns/CustomSelect";
import NewInputField from "../../../DesignSystem/Inputs/NewInputField";
import NewDateField from "../../../DesignSystem/Inputs/NewDateField";
import NewTextArea from "../../../DesignSystem/Inputs/NewTextArea";
import RadioGroup from "../../../DesignSystem/Inputs/RadioGroup";
import PrimaryButton from "../../../DesignSystem/Buttons/PrimaryButton";
import SecondaryButton from "../../../DesignSystem/Buttons/SecondaryButton";
import {
  createOneTimeManualCharge,
  createRecurringManualCharge,
  deleteManualCharge,
  subscribeMonthlyManualChargeEditLogs,
  updateOneTimeManualCharge,
  updateRecurringManualCharge,
} from "../services/discountChargeService";
import {
  isAmlGuidelineCharge,
  MANUAL_CHARGE_TYPES,
} from "../constants/guidelineCharges";
import { getBankOfEnglandRate } from "../utils/bankRate";
import {
  formatChargeDateTime,
  formFromManualCharge,
  LOG_FIELD_LABELS,
  MANUAL_CHARGE_TYPE_OPTIONS,
  priceIncreaseDescription,
} from "../utils/manualChargeForm";
import {
  calculateCurrentRepaymentInstalment,
  calculateLoanChargeTo,
  calculateLoanRepaymentSchedule,
} from "../utils/loanRepayment";
import "../../../../css/Praplanning/ParaplanningDashboard.css";
import "../../../../css/Adviser/AdviserDetailsPage.css";
import "../../../../css/Marketing/WebsiteCharges.css";
import { ReactComponent as CloseIcon } from "../../../../assets/icons/close.svg";

const AddCharge = ({
  adviser,
  onClose,
  charge,
  asPage = false,
  defaultDate,
  defaultRecurringDate,
  allowLoanRepayment = true,
  userPermissions = [],
}) => {
  const [activePanel, setActivePanel] = useState("charge");
  const [billingType, setBillingType] = useState(
    charge?.billingType === "recurring" ? "recurring" : "one-time",
  );
  const [form, setForm] = useState(() =>
    formFromManualCharge(charge, defaultDate, defaultRecurringDate),
  );
  const isAmlCharge = isAmlGuidelineCharge({
    chargeTypeId: form.chargeType,
  });
  const isSmartSearch = form.chargeType === "smartsearch";
  const isLoanRepayment = form.chargeType === "loan-repayment";
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [bankRateAsOf, setBankRateAsOf] = useState("");
  const [bankRateLoading, setBankRateLoading] = useState(false);
  const [bankRateHistory, setBankRateHistory] = useState([]);
  const [currentPaymentOverridden, setCurrentPaymentOverridden] = useState(
    () =>
      Boolean(
        charge?.currentMonthlyRepaymentAmount != null &&
          Number(charge.currentMonthlyRepaymentAmount) !==
            Number(charge.monthlyRepaymentAmount),
      ),
  );
  const [monthlyInterestRateOverridden, setMonthlyInterestRateOverridden] =
    useState(() => Boolean(charge?.monthlyInterestRatePm != null));
  const [monthlyPaymentOverridden, setMonthlyPaymentOverridden] = useState(
    () => {
      const total = Number(charge?.amountCharged);
      const instalments = Number(charge?.repaymentInstalments);
      const calculated = instalments > 0 ? total / instalments : 0;
      return (
        charge?.monthlyRepaymentAmount != null &&
        Math.abs(Number(charge.monthlyRepaymentAmount) - calculated) > 0.005
      );
    },
  );
  const [logs, setLogs] = useState([]);
  const isEditing = Boolean(charge?.id);
  const chargeTypeOptions = useMemo(
    () =>
      allowLoanRepayment
        ? MANUAL_CHARGE_TYPE_OPTIONS
        : MANUAL_CHARGE_TYPE_OPTIONS.filter(
            (option) => option.id !== "loan-repayment",
          ),
    [allowLoanRepayment],
  );
  const chargeId = charge?.id || "";
  const latestChargeRef = useRef(charge);
  latestChargeRef.current = charge;
  const createdLog =
    charge?.createdAt && !logs.some((log) => log.action === "created")
      ? {
          id: "created",
          action: "created",
          updatedAt: charge.createdAt,
          updatedByName: charge.createdBy?.name || "",
          updatedBy: charge.createdBy?.email || "",
          changes: [],
        }
      : null;
  const displayedLogs = createdLog ? [...logs, createdLog] : logs;
  const formattedBankRateDate = bankRateAsOf
    ? new Date(`${bankRateAsOf}T00:00:00.000Z`).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "2-digit",
        timeZone: "UTC",
      })
    : "";
  const money = (value) =>
    Number(value || 0).toLocaleString("en-GB", {
      style: "currency",
      currency: "GBP",
    });
  const formatPaymentDate = (value) => {
    const [year, month, day] = String(value || "").slice(0, 10).split("-");
    return year && month && day ? `${day}/${month}/${year}` : "-";
  };
  useEffect(() => {
    const selectedCharge = latestChargeRef.current;
    setBillingType(
      selectedCharge?.billingType === "recurring" ? "recurring" : "one-time",
    );
    setForm(
      formFromManualCharge(selectedCharge, defaultDate, defaultRecurringDate),
    );
    setCurrentPaymentOverridden(
      Boolean(
        selectedCharge?.currentMonthlyRepaymentAmount != null &&
          Number(selectedCharge.currentMonthlyRepaymentAmount) !==
            Number(selectedCharge.monthlyRepaymentAmount),
      ),
    );
    setMonthlyInterestRateOverridden(
      Boolean(selectedCharge?.monthlyInterestRatePm != null),
    );
    const total = Number(selectedCharge?.amountCharged);
    const instalments = Number(selectedCharge?.repaymentInstalments);
    const calculated = instalments > 0 ? total / instalments : 0;
    setMonthlyPaymentOverridden(
      selectedCharge?.monthlyRepaymentAmount != null &&
        Math.abs(Number(selectedCharge.monthlyRepaymentAmount) - calculated) >
          0.005,
    );
  }, [chargeId, defaultDate, defaultRecurringDate]);
  useEffect(() => {
    setActivePanel("charge");
  }, [chargeId]);
  useEffect(() => {
    if (!charge?.id) {
      setLogs([]);
      return undefined;
    }
    return subscribeMonthlyManualChargeEditLogs(charge.id, setLogs, () =>
      setLogs([]),
    );
  }, [charge?.id]);
  useEffect(() => {
    if (billingType !== "recurring" || !isLoanRepayment) return undefined;
    let active = true;
    setBankRateLoading(true);
    getBankOfEnglandRate({ includeHistory: true })
      .then((result) => {
        if (!active) return;
        const rate = Number(result?.rate);
        if (Number.isFinite(rate)) {
          setForm((current) => ({
            ...current,
            annualBoeBaseRatePa: String(rate),
          }));
        }
        setBankRateAsOf(result?.asOf || "");
        setBankRateHistory(Array.isArray(result?.history) ? result.history : []);
      })
      .catch(() => {
        if (active)
          setError("The Bank of England Bank Rate could not be loaded.");
      })
      .finally(() => {
        if (active) setBankRateLoading(false);
      });
    return () => {
      active = false;
    };
  }, [billingType, isLoanRepayment]);
  const loanTermsLocked = isEditing && isLoanRepayment;
  useEffect(() => {
    if (!isLoanRepayment || (loanTermsLocked && form.chargeTo)) return;
    const chargeTo = calculateLoanChargeTo(
      form.chargeFrom,
      form.repaymentInstalments,
    );
    setForm((current) =>
      current.chargeTo === chargeTo ? current : { ...current, chargeTo },
    );
  }, [
    form.chargeFrom,
    form.chargeTo,
    form.repaymentInstalments,
    isLoanRepayment,
    loanTermsLocked,
  ]);
  const loanProjection = useMemo(
    () =>
      calculateLoanRepaymentSchedule({
        ...form,
        bankRateHistory,
      }),
    [form, bankRateHistory],
  );
  const firstProjectedRepayment = loanProjection.schedule[0];
  const calculatedMonthlyInterestRatePm =
    (Number(form.annualInterestRatePa || 0) +
      Number(form.annualBoeBaseRatePa || 0)) /
    12;
  useEffect(() => {
    if (monthlyInterestRateOverridden) return;
    const calculatedValue = calculatedMonthlyInterestRatePm.toFixed(4);
    setForm((current) =>
      current.monthlyInterestRatePm === calculatedValue
        ? current
        : { ...current, monthlyInterestRatePm: calculatedValue },
    );
  }, [calculatedMonthlyInterestRatePm, monthlyInterestRateOverridden]);
  const currentInstalment = useMemo(
    () =>
      calculateCurrentRepaymentInstalment({
        chargeFrom: form.chargeFrom,
        repaymentInstalments: form.repaymentInstalments,
      }),
    [form.chargeFrom, form.repaymentInstalments],
  );
  const currentProjectedRepayment =
    loanProjection.schedule[Math.max(0, currentInstalment.current - 1)] ||
    firstProjectedRepayment;
  const paymentSchedule = loanProjection.schedule.length
    ? loanProjection.schedule
    : charge?.repaymentSchedule || [];
  useEffect(() => {
    if (!isLoanRepayment || loanTermsLocked || monthlyPaymentOverridden) return;
    const total = Number(form.totalAmount);
    const instalments = Number(form.repaymentInstalments);
    const monthlyPayment =
      total > 0 && instalments > 0 ? (total / instalments).toFixed(2) : "";
    setForm((current) =>
      current.monthlyRepaymentAmount === monthlyPayment
        ? current
        : { ...current, monthlyRepaymentAmount: monthlyPayment },
    );
  }, [
    form.repaymentInstalments,
    form.totalAmount,
    isLoanRepayment,
    loanTermsLocked,
    monthlyPaymentOverridden,
  ]);
  useEffect(() => {
    if (!isLoanRepayment || currentPaymentOverridden) return;
    setForm((current) =>
      current.currentMonthlyRepaymentAmount === current.monthlyRepaymentAmount
        ? current
        : {
            ...current,
            currentMonthlyRepaymentAmount: current.monthlyRepaymentAmount,
          },
    );
  }, [form.monthlyRepaymentAmount, currentPaymentOverridden, isLoanRepayment]);
  const modalTabs = [
    { id: "charge", label: "Charge" },
    ...(isEditing && paymentSchedule.length
      ? [{ id: "payment-history", label: "Payment History" }]
      : []),
    ...(isEditing ? [{ id: "logs", label: "Logs" }] : []),
  ];
  const update = (key) => (event) =>
    setForm((current) => ({ ...current, [key]: event.target.value }));
  const updateChargeType = (event) => {
    const chargeType = event.target.value;
    const guidelineCharge = MANUAL_CHARGE_TYPES.find(
      (charge) => charge.id === chargeType,
    );
    const nextBillingType =
      guidelineCharge?.billingType ||
      (chargeType === "loan-repayment" ? "recurring" : "one-time");

    setForm((current) => ({
      ...current,
      chargeType,
      checkType: guidelineCharge?.checkType || "",
      client: isAmlGuidelineCharge({ chargeTypeId: chargeType })
        ? current.client
        : "",
      totalAmount:
        typeof guidelineCharge?.amount === "number"
          ? String(guidelineCharge.amount)
          : chargeType === "knowledge-bank" &&
            nextBillingType === "one-time" &&
            !isEditing
            ? "4.8"
            : "",
      description:
        current.description ||
        (chargeType === "knowledge-bank" &&
          nextBillingType === "one-time" &&
          !isEditing
          ? priceIncreaseDescription(current.date)
          : ""),
    }));
    setBillingType(nextBillingType);
  };
  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      if (isLoanRepayment && !allowLoanRepayment) {
        throw new Error("You do not have permission to access Loan Repayment charges.");
      }
      const selectedChargeType = MANUAL_CHARGE_TYPES.find(
        (charge) => charge.id === form.chargeType,
      );
      const values = {
        adviserEmail: adviser.email,
        adviserName: adviser.name,
        adviserId: adviser.id,
        brandName: adviser.brand,
        userPermissions,
        ...form,
        amount: form.totalAmount,
        chargeType: selectedChargeType?.label || form.chargeType,
        chargeTypeId: selectedChargeType?.id || form.chargeType,
      };
      if (billingType === "recurring") {
        if (isLoanRepayment && !loanProjection.fullyRepaid) {
          throw new Error(
            "The current monthly repayment must exceed the accrued interest and repay the loan within 600 months.",
          );
        }
        const recurringValues = {
          ...values,
          bankRateAsOf,
          monthlyInterestRatePm: form.monthlyInterestRatePm,
          monthlyInterestAmount: firstProjectedRepayment?.interestAccrued || 0,
          currentBalanceRemaining:
            currentProjectedRepayment?.openingBalance || Number(form.totalAmount),
          currentRepaymentInstalment: currentInstalment.label,
          totalRepaymentAmount: loanProjection.totalRepaymentAmount,
          totalInterest: loanProjection.totalInterest,
          repaymentSchedule: loanProjection.schedule,
        };
        if (isEditing) {
          await updateRecurringManualCharge({
            chargeId: charge.id,
            previousCharge: charge,
            ...recurringValues,
          });
        } else {
          await createRecurringManualCharge(recurringValues);
        }
      } else if (isEditing) {
        await updateOneTimeManualCharge({
          chargeId: charge.id,
          previousCharge: charge,
          ...values,
        });
      } else {
        await createOneTimeManualCharge(values);
      }
      onClose();
    } catch (saveError) {
      setError(saveError.message || "The charge could not be saved.");
    } finally {
      setSaving(false);
    }
  };
  const removeCharge = async () => {
    if (!isEditing) {
      onClose();
      return;
    }

    setSaving(true);
    setError("");
    try {
      await deleteManualCharge(charge.id, userPermissions);
      onClose();
    } catch (deleteError) {
      setError(deleteError.message || "The charge could not be removed.");
    } finally {
      setSaving(false);
    }
  };

  const chargeForm = (
    <form
      className={
        asPage ? "ParaplanningModal add-charge-page-card" : "ParaplanningModal"
      }
      role={asPage ? undefined : "dialog"}
      aria-modal={asPage ? undefined : "true"}
      aria-label="Add new charge"
      onSubmit={submit}
      onMouseDown={(event) => event.stopPropagation()}
    >
      {!asPage ? (
        <div className="ParaplanningModal__Header">
          <h2 className="FP_H3">
            {charge?.manualChargeType || charge?.chargeType || "Add New Charge"}
          </h2>
          <button type="button" aria-label="Close" onClick={onClose}>
            <CloseIcon />
          </button>
        </div>
      ) : null}
      {!asPage || isEditing ? (
        <Tabs
          tabs={modalTabs}
          activeTab={activePanel}
          onTabChange={setActivePanel}
          noShadow
        />
      ) : null}
      {activePanel === "payment-history" ? (
        <div className="website-table-wrap table-scroll-container">
          <table className="website-table" aria-label="Payment History">
            <thead>
              <tr>
                {[
                  "Instalment Number",
                  "Payment Date",
                  "Opening Balance",
                  "BoE Rate",
                  "Interest Accrued",
                  "Expected Payment",
                  "Actual Payment",
                  "Applied to Principal",
                  "Closing Balance",
                ].map((label) => (
                  <th key={label} className="FP_Help_Text_Semibold uppercase">
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paymentSchedule.map((entry) => {
                const isCurrent =
                  entry.instalment === currentInstalment.current;
                const cellClass = isCurrent
                  ? "FP_Help_Text_Semibold black"
                  : "FP_Help_Text black";
                return (
                  <tr key={`${entry.instalment}-${entry.paymentDate}`}>
                    <td className={cellClass}>
                      {entry.instalment}/
                      {charge?.repaymentInstalments ||
                        form.repaymentInstalments ||
                        paymentSchedule.length}
                    </td>
                    <td className={cellClass}>
                      {formatPaymentDate(entry.paymentDate)}
                    </td>
                    <td className={cellClass}>{money(entry.openingBalance)}</td>
                    <td className={cellClass}>{entry.bankOfEnglandRate}%</td>
                    <td className={cellClass}>{money(entry.interestAccrued)}</td>
                    <td className={cellClass}>
                      {money(entry.expectedMonthlyRepayment)}
                    </td>
                    <td className={cellClass}>
                      {money(entry.actualMonthlyRepayment)}
                    </td>
                    <td className={cellClass}>
                      {money(entry.appliedToPrincipal)}
                    </td>
                    <td className={cellClass}>{money(entry.closingBalance)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : activePanel === "logs" ? (
        <div className="lcEditLogsList">
          {!displayedLogs.length ? (
            <p className="FP_Content grey">No edit logs found yet.</p>
          ) : null}
          {displayedLogs.map((log, index) => {
            const changes = Array.isArray(log.changes) ? log.changes : [];
            return (
              <div key={log.id || index} className="lcEditLogItem">
                <div className="lcEditLogHeader">
                  <span className="FP_Help_Text_Semibold">
                    {log.action === "created" ? "Added by " : "Updated by "}
                    {log.updatedByName ||
                      log.updatedBy ||
                      log.editedBy ||
                      "Unknown user"}
                  </span>
                  <span className="FP_Help_Text muted">
                    {formatChargeDateTime(log.updatedAt || log.editedAt)}
                  </span>
                </div>
                <div className="lcEditLogChanges">
                  {changes.map((change, changeIndex) => (
                    <div
                      key={`${log.id || index}-${change.fieldPath || change.field || changeIndex}`}
                      className="lcEditLogChange"
                    >
                      <div className="FP_Help_Text_Semibold">
                        {change.label ||
                          LOG_FIELD_LABELS[change.fieldPath] ||
                          change.fieldPath ||
                          change.field ||
                          "Charge updated"}
                      </div>
                      <div className="FP_Help_Text">
                        <span className="muted">From:</span>{" "}
                        {change.previousValue ?? "-"}
                      </div>
                      <div className="FP_Help_Text">
                        <span className="muted">To:</span>{" "}
                        {change.newValue ?? "-"}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <>
          <div className="ParaplanningModal__Grid">
            <CustomSelect
              label="Adviser Name"
              value={adviser.email}
              selectedLabel={adviser.name}
              options={[{ id: adviser.email, name: adviser.name }]}
              searchable={false}
              clearable={false}
              disabled
            />
            <NewDateField
              label="Date"
              value={form.date}
              onChange={update("date")}
              required
            />
            <CustomSelect
              label="Charge Type"
              value={form.chargeType}
              onChange={updateChargeType}
              options={chargeTypeOptions}
              placeholder="Select charge type"
              searchable={false}
              clearable={false}
              required
            />
            <RadioGroup
              name="manual-charge-billing-type"
              options={[
                { value: "one-time", label: "One-Time Charge" },
                {
                  value: "recurring",
                  label: "Recurring Charge",
                  disabled: isAmlCharge,
                },
              ]}
              value={billingType}
              onChange={(event) => {
                const nextBillingType = event.target.value;
                setBillingType(nextBillingType);
                if (
                  !isEditing &&
                  form.chargeType === "knowledge-bank" &&
                  nextBillingType === "one-time"
                ) {
                  setForm((current) => ({
                    ...current,
                    totalAmount: "4.8",
                    description:
                      current.description ||
                      priceIncreaseDescription(current.date),
                  }));
                }
              }}
              inline
            />
          </div>
          {isAmlCharge ? (
            <div className="ParaplanningModal__Grid">
              <NewInputField
                id="manual-charge-client"
                label="Client Name"
                type="text"
                value={form.client}
                onChange={update("client")}
                placeholder="Enter client name"
                required
              />
              {isSmartSearch ? (
                <NewInputField
                  id="manual-charge-check-type"
                  label="Check Type"
                  type="text"
                  value={form.checkType}
                  readOnly
                  required
                />
              ) : null}
            </div>
          ) : null}
          <NewTextArea
            id="manual-charge-description"
            label="Charge Description"
            value={form.description}
            onChange={update("description")}
            placeholder="Enter charge description"
            required={isAmlCharge}
          />
          {billingType === "recurring" ? (
            <div className="ParaplanningModal__Grid ParaplanningModal__Grid--four">
              <NewInputField
                id="manual-charge-amount"
                label="Total Amount (£)"
                type="number"
                value={form.totalAmount}
                onChange={update("totalAmount")}
                placeholder="0.00"
                required
                readOnly={loanTermsLocked}
              />
              <NewDateField
                id="manual-charge-from"
                label="Charge From"
                value={form.chargeFrom}
                onChange={update("chargeFrom")}
                readOnly={loanTermsLocked}
              />
              <NewDateField
                id="manual-charge-to"
                label="Charge To"
                value={form.chargeTo}
                onChange={update("chargeTo")}
                readOnly={loanTermsLocked}
              />
              {isLoanRepayment ? (
                <>
                  <NewInputField
                    id="manual-charge-installments"
                    label="Repayment Instalments"
                    type="number"
                    value={form.repaymentInstalments}
                    onChange={update("repaymentInstalments")}
                    placeholder="Enter instalments"
                    readOnly={loanTermsLocked}
                  />
                  <NewInputField
                    id="manual-charge-annual-interest"
                    label="Annual Interest Rate p.a. (%)"
                    type="number"
                    value={form.annualInterestRatePa}
                    onChange={update("annualInterestRatePa")}
                    placeholder="Interest p.a."
                    readOnly={loanTermsLocked}
                  />
                  <NewInputField
                    id="manual-charge-bank-rate"
                    label="Annual BoE Base Rate p.a. (%)"
                    type="number"
                    value={form.annualBoeBaseRatePa}
                    onChange={update("annualBoeBaseRatePa")}
                    placeholder={bankRateLoading ? "Loading…" : "0.00"}
                    tooltip={
                      formattedBankRateDate
                        ? `Rate as at ${formattedBankRateDate}`
                        : ""
                    }
                    readOnly
                  />
                  {/* <NewInputField id="manual-charge-annual-loan-fee" label="Annual Basic Loan Fee per Annum (£)" type="number" value={form.annualBasicLoanFee} onChange={update("annualBasicLoanFee")} placeholder="0.00" /> */}
                  <NewInputField
                    id="manual-charge-monthly-interest-rate"
                    label="Monthly Interest Rate per month (%)"
                    type="number"
                    value={form.monthlyInterestRatePm}
                    onChange={(event) => {
                      setMonthlyInterestRateOverridden(true);
                      update("monthlyInterestRatePm")(event);
                    }}
                    placeholder="0.00"
                  />
                  <NewInputField
                    id="manual-charge-monthly-interest"
                    label="Monthly Interest Amount (£)"
                    type="number"
                    value={firstProjectedRepayment?.interestAccrued || 0}
                    onChange={update("monthlyInterestAmount")}
                    placeholder="0.00"
                    readOnly
                  />
                  <NewInputField
                    id="manual-charge-monthly-chargeable"
                    label="Monthly Repayment Amount (£)"
                    type="number"
                    value={form.monthlyRepaymentAmount}
                    onChange={(event) => {
                      const total = Number(form.totalAmount);
                      const instalments = Number(form.repaymentInstalments);
                      const calculated = instalments > 0 ? total / instalments : 0;
                      setMonthlyPaymentOverridden(
                        Math.abs(Number(event.target.value) - calculated) > 0.005,
                      );
                      update("monthlyRepaymentAmount")(event);
                    }}
                    placeholder="0.00"
                    readOnly={loanTermsLocked}
                  />
                  <NewInputField
                    id="manual-charge-current-monthly"
                    label="Current Monthly Repayment Amount (£)"
                    type="number"
                    value={form.currentMonthlyRepaymentAmount}
                    onChange={(event) => {
                      setCurrentPaymentOverridden(
                        Number(event.target.value) !==
                          Number(form.monthlyRepaymentAmount),
                      );
                      update("currentMonthlyRepaymentAmount")(event);
                    }}
                    placeholder="0.00"
                  />
                  <NewInputField
                    id="manual-charge-current-installments"
                    label="Current Repayment Instalment"
                    type="text"
                    value={currentInstalment.label || form.currentRepaymentInstalment}
                    onChange={update("currentRepaymentInstalment")}
                    placeholder="e.g. 3/12"
                    readOnly
                  />
                  <NewInputField
                    id="manual-charge-balance"
                    label="Current Balance Remaining (£)"
                    type="number"
                    value={
                      currentProjectedRepayment?.openingBalance ||
                      form.currentBalanceRemaining
                    }
                    onChange={update("currentBalanceRemaining")}
                    placeholder="0.00"
                    readOnly
                  />
                  <NewInputField
                    id="manual-charge-total-repayment"
                    label="Total Repayment Amount (£)"
                    type="number"
                    value={loanProjection.totalRepaymentAmount || 0}
                    readOnly
                  />
                </>
              ) : null}
            </div>
          ) : (
            <NewInputField
              id="manual-charge-amount"
              label="Total Amount (£)"
              type="number"
              value={form.totalAmount}
              onChange={update("totalAmount")}
              placeholder="0.00"
              required
            />
          )}
          <NewTextArea
            id="manual-charge-notes"
            label="Additional Notes"
            value={form.notes}
            onChange={update("notes")}
            placeholder="Additional notes"
          />
          {error ? (
            <p className="FP_Content text-error" role="alert">
              {error}
            </p>
          ) : null}
          <div className="ParaplanningModal__Actions">
            <SecondaryButton
              type="button"
              label={saving ? "Removing…" : "Remove Charge"}
              onClick={removeCharge}
              disabled={saving}
            />
            <PrimaryButton
              type="submit"
              label={
                saving ? "Saving…" : isEditing ? "Save Changes" : "Add Charge"
              }
              disabled={saving}
            />
          </div>
        </>
      )}
    </form>
  );

  if (asPage) return chargeForm;

  return (
    <div
      className="ParaplanningModal__Host"
      role="presentation"
      onMouseDown={onClose}
    >
      {chargeForm}
    </div>
  );
};

export default AddCharge;
